import { memo, useState, useCallback } from 'react';
import type { IGeneratedPost } from '../../@types/posts';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, Edit3, Sparkles } from 'lucide-react';
import { PostEditorMemo } from './PostEditor';
import { AIPostEditorMemo } from './AIPostEditor';

/**
 * Combines all post sections into a single text INCLUDING hashtags
 * Order: Hook → Problem → Education → Authority → CTA → Hashtags
 */
function combinePostSections(post: IGeneratedPost): string {
  const sections = [
    post.hook,
    post.problemSetup,
    post.educationalContent,
    post.authorityStatement,
    post.callToAction,
  ];

  const postText = sections.filter(section => section.trim().length > 0).join('\n\n');

  // Add hashtags at the end
  const hashtagsText = post.hashtags.join(' ');
  return postText + '\n\n' + hashtagsText;
}

interface IGeneratedPostDisplayProps {
  post: IGeneratedPost;
  onPostUpdated: (editedContent: string) => void;
}

const GeneratedPostDisplay = ({ post, onPostUpdated }: IGeneratedPostDisplayProps) => {
  // State management
  const [editMode, setEditMode] = useState<'view' | 'manual' | 'ai'>('view');
  const [optimisticContent, setOptimisticContent] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // Computed values
  const formattedDate = new Date(post.generatedAt).toLocaleString();
  const displayText = optimisticContent || post.editedContent || combinePostSections(post);

  // Event handlers
  const handleManualEditClick = useCallback(() => {
    setEditMode('manual');
  }, []);

  const handleAIEditClick = useCallback(() => {
    setEditMode('ai');
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditMode('view');
  }, []);

  const handleSaveSuccess = useCallback(
    (editedContent: string) => {
      // Optimistically update display
      setOptimisticContent(editedContent);
      setEditMode('view');

      // Update parent state
      onPostUpdated(editedContent);

      // Clear optimistic state after a delay
      setTimeout(() => {
        setOptimisticContent(null);
      }, 1000);
    },
    [onPostUpdated]
  );

  const handleCopyText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(displayText);
      setIsCopied(true);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch {
      // Silently fail if clipboard API is not available
    }
  }, [displayText]);

  // Render
  return (
    <div className="space-y-6">
      {/* Header with metadata */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{post.postTitle}</h2>
          <Badge variant="outline">{post.status}</Badge>
        </div>

        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>Generated: {formattedDate}</span>
          {post.tokensUsed && <span>Tokens: {post.tokensUsed}</span>}
          <span>Model: {post.modelVersion}</span>
        </div>

        {post.editedContent && <Badge variant="secondary">Edited</Badge>}
      </div>

      <Separator />

      {/* Post Content - View, Manual Edit, or AI Edit Mode */}
      {editMode === 'view' && (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleCopyText}
              variant="ghost"
              size="icon"
              title={isCopied ? 'Copied!' : 'Copy text'}
            >
              {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button onClick={handleManualEditClick} variant="outline">
              <Edit3 className="mr-2 h-4 w-4" />
              Edit Manually
            </Button>
            <Button onClick={handleAIEditClick} variant="default">
              <Sparkles className="mr-2 h-4 w-4" />
              Edit with AI
            </Button>
          </div>
          <p className="whitespace-pre-wrap text-base leading-relaxed">{displayText}</p>
        </div>
      )}

      {editMode === 'manual' && (
        <PostEditorMemo
          key={post._id}
          postId={post._id}
          initialContent={displayText}
          onSaveSuccess={handleSaveSuccess}
          onCancel={handleCancelEdit}
        />
      )}

      {editMode === 'ai' && (
        <AIPostEditorMemo
          postId={post._id}
          originalContent={displayText}
          onSaveSuccess={handleSaveSuccess}
          onCancel={handleCancelEdit}
        />
      )}
    </div>
  );
};

export const GeneratedPostDisplayMemo = memo(GeneratedPostDisplay);
